import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DataTable from '../../components/Table/DataTable';
import SearchBox from '../../components/SearchBox/SearchBox';

import { getBuyerClientsInGateway, createBuyerClientInGateway, updateBuyerClientInGateway } from '../../services/buyerClientsInGateway';
import { showAlert } from '../../store/ducks/alert';

import { CircularProgress } from '@material-ui/core';
import { Button, InputField, Modal, Text } from '@getpingback/realeza';
import { Edit as EditIcon, Add as AddIcon } from '@material-ui/icons';

const headers = ['id', 'channelUid', 'tokenIdInGateway', 'paymentOriginId', 'createdAt'];

const BuyerClientsInGateway = () => {
  const dispatch = useDispatch();

  const [buyerClients, setBuyerClients] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOrdering, setIsOrdering] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchField, setSearchField] = useState({ searchItem: '', search: '' });
  const [orderDirections, setOrderDirections] = useState({ item: '', direction: '' });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [channelUidValue, setChannelUidValue] = useState('');
  const [tokenIdInGatewayValue, setTokenIdInGatewayValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    displayBuyerClients(page);
  }, [isOrdering, isSearching, page]);

  const handleSubmitSearch = searchBox => {
    setSearchField({ searchItem: searchBox.searchItem, search: searchBox.search });
    setIsSearching(searchBox.search);
  };

  const displayBuyerClients = async currentPage => {
    try {
      setIsLoading(true);
      const params = isSearching ? { searchTerm: searchField.search, searchItem: searchField.searchItem } : {};
      const { data } = await getBuyerClientsInGateway(currentPage || page, params.searchTerm, params.searchItem);
      setBuyerClients(data.rows);
      setTotalItems(data.count);
      setTotalPages(data.totalPages);
      setPage(data.page);
      setIsLoading(false);
    } catch (error) {
      dispatch(showAlert('error', error.message));
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setChannelUidValue('');
    setTokenIdInGatewayValue('');
    setIsCreateModalOpen(true);
  };

  const openUpdateModal = item => {
    setSelectedItem(item);
    setTokenIdInGatewayValue(item.tokenIdInGateway || '');
    setIsUpdateModalOpen(true);
  };

  const handleCreate = async () => {
    try {
      setIsSaving(true);
      await createBuyerClientInGateway(channelUidValue, tokenIdInGatewayValue);
      dispatch(showAlert('success', 'Buyer client created successfully'));
      setIsCreateModalOpen(false);
      displayBuyerClients(page);
    } catch (error) {
      dispatch(showAlert('error', error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setIsSaving(true);
      await updateBuyerClientInGateway(selectedItem.channelUid, tokenIdInGatewayValue);
      dispatch(showAlert('success', 'Buyer client updated successfully'));
      setIsUpdateModalOpen(false);
      displayBuyerClients(page);
    } catch (error) {
      dispatch(showAlert('error', error.message));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <CircularProgress color="inherit" className="circular-progress" />;

  return (
    <>
      <div className="users-container">
        <h1>Buyer Clients In Gateway ({totalItems})</h1>

        <SearchBox
          selectItems={['id', 'channelUid', 'tokenIdInGateway']}
          onSubmitSearch={handleSubmitSearch}
          isSearching={isSearching}
          setIsOrdering={setIsOrdering}
        />

        <Button appearence="solid" mt="16px" onClick={openCreateModal}>
          <AddIcon /> Add Buyer
        </Button>
      </div>

      <DataTable
        useUid={true}
        head={headers}
        body={buyerClients}
        action={false}
        bodyColumn={headers}
        linkPath={'stripe-sync-buyers'}
        totalPages={totalPages}
        currentPage={page}
        orderDirections={orderDirections}
        setOrderDirections={setOrderDirections}
        setPageChange={setPage}
        setIsOrdering={setIsOrdering}
        isOrdering={isOrdering}
        customActions={[
          {
            icon: <EditIcon />,
            action: item => openUpdateModal(item),
          },
        ]}
      />

      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
        <Text tag="h2" type="subtitle" size={20} weight="semibold">
          Add Buyer Client
        </Text>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <InputField
            label="Channel UID"
            value={channelUidValue}
            onChange={e => setChannelUidValue(e.target.value)}
            placeholder="Enter channel UID"
          />

          <InputField
            label="Token ID in Gateway"
            value={tokenIdInGatewayValue}
            onChange={e => setTokenIdInGatewayValue(e.target.value)}
            placeholder="Enter token ID in gateway"
          />
        </div>

        <Button fullWidth appearence="solid" mt="16px" isLoading={isSaving} onClick={handleCreate}>
          Create
        </Button>
      </Modal>

      <Modal isOpen={isUpdateModalOpen} onClose={() => setIsUpdateModalOpen(false)}>
        <Text tag="h2" type="subtitle" size={20} weight="semibold">
          Update Buyer Client
        </Text>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <InputField
            label="Token ID in Gateway"
            value={tokenIdInGatewayValue}
            onChange={e => setTokenIdInGatewayValue(e.target.value)}
            placeholder="Enter token ID in gateway"
          />
        </div>

        <Button fullWidth appearence="solid" mt="16px" isLoading={isSaving} onClick={handleUpdate}>
          Update
        </Button>
      </Modal>
    </>
  );
};

export default BuyerClientsInGateway;
